/**
 * 2.1 bloque C — Búsqueda IA: calcula los vectores de los anuncios que ya existían (los nuevos y los
 * editados se calculan solos con el trigger `ai-onProductEmbedding`). Va por lotes de 200 con
 * `ai-backfillEmbeddings`; es idempotente (un anuncio con el mismo texto no se vuelve a pagar) y se
 * puede parar y seguir. «Simular» cuenta sin gastar.
 */
import { useRef, useState, type ReactElement } from 'react';
import { api } from '@nuvvu/shared/api';
import type { BackfillEmbeddingsOutput } from '@nuvvu/shared';
import { call, errorText } from '../api.js';
import { btn, card, muted, Notice } from '../lib/ui.js';

type Status = 'active' | 'sold';

interface Progress {
  status: Status;
  scanned: number;
  embedded: number;
  skipped: number;
  chars: number;
  done: boolean;
}

/** Vertex: ≈ 0,000025 € por 1.000 caracteres (orientativo). */
const eur = (chars: number): string => ((chars / 1000) * 0.000025).toFixed(4);

export function SmartSearchPage(): ReactElement {
  const [runs, setRuns] = useState<Record<Status, Progress | null>>({ active: null, sold: null });
  const [busy, setBusy] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stop = useRef(false);
  // El botón «Parar» cambia la ref mientras el bucle espera a la red.
  const stopped = (): boolean => stop.current;

  const run = async (status: Status, dry: boolean): Promise<void> => {
    setBusy(status);
    setError(null);
    stop.current = false;
    let cursor: string | null = null;
    const acc: Progress = { status, scanned: 0, embedded: 0, skipped: 0, chars: 0, done: false };
    try {
      do {
        const r: BackfillEmbeddingsOutput = await call(api.ai.backfillEmbeddings, {
          status,
          cursor,
          limit: 200,
          dry_run: dry,
        });
        acc.scanned += r.scanned;
        acc.embedded += r.embedded;
        acc.skipped += r.skipped;
        acc.chars += r.chars;
        cursor = r.next_cursor;
        acc.done = cursor === null;
        setRuns((p) => ({ ...p, [status]: { ...acc } }));
      } while (cursor && !stopped());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const row = (status: Status, label: string): ReactElement => {
    const p = runs[status];
    return (
      <div style={card}>
        <strong>{label}</strong>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            style={btn(true)}
            disabled={busy !== null}
            onClick={() => void run(status, false)}
          >
            Calcular vectores
          </button>
          <button
            type="button"
            style={btn(false)}
            disabled={busy !== null}
            onClick={() => void run(status, true)}
          >
            Simular (sin gastar)
          </button>
          {busy === status && (
            <button
              type="button"
              style={btn(false, true)}
              onClick={() => {
                stop.current = true;
              }}
            >
              Parar
            </button>
          )}
        </div>
        {p && (
          <p style={muted}>
            Revisados {p.scanned} · nuevos/cambiados {p.embedded} · ya al día {p.skipped} ·{' '}
            {p.chars.toLocaleString('es-ES')} caracteres (≈ {eur(p.chars)} €)
            {p.done ? ' · terminado' : busy === status ? ' · en curso…' : ' · parado'}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <h1 style={{ margin: 0 }}>Búsqueda IA</h1>
      <p style={muted}>
        Vectores de los anuncios para «Búsqueda inteligente», «Artículos parecidos» y «Precio
        sugerido» (Vertex AI, modelo multilingüe; se guardan en `productEmbeddings`). Los anuncios
        nuevos o editados se calculan solos mientras alguno de esos tres interruptores esté
        encendido (o en «Solo admins»). Esto es para los que ya existían: hazlo una vez al encender
        y repite si cambias de modelo. Los vendidos sirven para el precio sugerido.
      </p>
      <Notice text={error} error />
      {row('active', 'Anuncios activos')}
      {row('sold', 'Anuncios vendidos')}
    </>
  );
}
